import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DoCheck, Input, SimpleChanges, OnInit, OnChanges, Output, EventEmitter, computed, input, signal } from '@angular/core';
import { DisplayOptions, CosmeticOptions, CosmeticPresets } from '../../models/filter-models';
import { DisplayType } from "../../models/enums";
import { assetBaseUrl, imgPrefix, poe2 } from '../../../game';
import { Rarity, LabelSize, Color, MinimapIconSize, scaleLabelSize } from "../../models/enums";
import { CosmeticsGeneratorService } from '../../services/cosmetics-generator.service';
import { FilterStoreService } from '../../services/filter-store.service';
import { ItemDataService } from '../../services/item-data.service';
import { SettingsService } from '../../services/settings.service';
import { labelPreviewBgImageStyle, resolveLabelPreviewBgId } from '../label-preview-backgrounds';
import { NavigationService } from '../../services/navigation.service';
import { CtrlKeyService } from '../../services/ctrl-key.service';

@Component({
  selector: '[app-label-viewer]',
  imports: [NgTemplateOutlet],
  templateUrl: './label-viewer.component.html',
  styleUrls: ['./label-viewer.component.scss', './label-viewer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.overflow-hidden]': '!disableBackground',
    '[class.stacked-effects-only]': 'previewLayer() === "effects"',
    '[class.stacked-label-only]': 'previewLayer() === "label"',
  },
})
export class LabelViewerComponent implements OnInit, OnChanges, DoCheck {
  @Input({ required: true }) displayOptions!: DisplayOptions;
  @Input() forceHidden?: boolean;
  @Input() forceVisible?: boolean;

  @Input() rareName?: string;
  exampleName = input<string>();
  stackSize = input<number>();
  displayName = computed(() => {
    const name = this.exampleName() || 'Example';
    const stack = this.stackSize();
    if (stack && stack > 1) {
      return name === 'Gold' ? `${stack} ${name}` : `${stack}x ${name}`;
    }
    return name;
  });
  @Input() unidentifiedItemTier?: number;
  @Input() rarity?: Rarity;
  @Input() sockets?: string;
  @Input() influence?: string;
  @Input() memoryStrands?: boolean;
  @Input() fractured?: boolean;
  @Input() veiled?: boolean;
  @Input() synthesised?: boolean;
  @Input() isCurrency?: boolean;
  @Input() isWaystone?: boolean;
  @Input() isPoe1Gem?: boolean;
  @Input() isDivinationCard?: boolean;
  @Input() isQuestItem?: boolean;

  @Input() disableBackground?: boolean;
  @Input() disableBeam?: boolean;
  glowDimmed = input(false);

  beamPhase: 'in' | 'out' | 'idle-visible' | 'idle-hidden' = 'idle-hidden';
  private beamWasRenderable = false;
  clickable = input(false);
  gearBaseType = input<string>();
  jewelleryBaseType = input<string>();

  @Input() dirtyForceUpdate?: number;
  forceGlow = input<boolean | null>(null);
  labelPreviewBgOverride = input<string | null>(null);
  previewLayer = input<'default' | 'effects' | 'label'>('default');

  exampleBgImage = computed(() =>
    labelPreviewBgImageStyle(
      resolveLabelPreviewBgId(this.labelPreviewBgOverride(), this.settingsService.labelPreviewBg())
    )
  );

  @Output() mouseEnterLabel = new EventEmitter<MouseEvent>();
  @Output() mouseLeaveLabel = new EventEmitter<MouseEvent>();
  @Output() clickLabel = new EventEmitter<MouseEvent>();

  imgPrefix = imgPrefix;
  assetBaseUrl = assetBaseUrl;
  highlightExample = false;
  showGearBaseTypeNav = false;
  showJewelleryBaseTypeNav = false;
  isGearBaseTypeHovered = signal(false);
  isJewelleryBaseTypeHovered = signal(false);
  private exampleNameIsGearBaseType = computed(() =>
    !!this.gearBaseType() && this.exampleName() === this.gearBaseType()
  );
  private exampleNameIsJewelleryBaseType = computed(() =>
    !!this.jewelleryBaseType() && this.exampleName() === this.jewelleryBaseType()
  );
  gearBaseTypeNavHovered = computed(() =>
    this.settingsService.enableRuleNavigation()
    && !!this.gearBaseType()
    && this.isGearBaseTypeHovered()
    && this.ctrlKeyService.isCtrlHeld()
  );
  jewelleryBaseTypeNavHovered = computed(() =>
    this.settingsService.enableRuleNavigation()
    && !!this.jewelleryBaseType()
    && this.isJewelleryBaseTypeHovered()
    && this.ctrlKeyService.isCtrlHeld()
  );

  Color = Color;
  MinimapIconSize = MinimapIconSize;
  DisplayType = DisplayType;

  cosmeticOptions: CosmeticOptions = new CosmeticOptions();
  hidden = false;
  disabledLabelStyle = false;
  defaultCosmeticOptions: CosmeticOptions = new CosmeticOptions();
  cosmeticPresets!: CosmeticPresets;
  itemColors!: { normal: string, magic: string, rare: string, unique: string, currency: string, gold: string, divinationCard: string };

  // Socket-related properties
  parsedSockets: { color: string; position: { row: number; col: number } }[] = [];
  socketsLinked = false;

  // Cached style objects — recomputed only when inputs change or on hover
  itemStyle: Record<string, string | null> = {};
  itemStyleDisabled: Record<string, string | null> = {};
  labelIconStyle: Record<string, string> = {};
  labelIconStyleDisabled: Record<string, string> = {};
  socketsContainerStyle: Record<string, string> = {};
  socketStyles: Record<string, string>[] = [];
  socketLinks: { from: { color: string; position: { row: number; col: number } }; to: { color: string; position: { row: number; col: number } } }[] = [];
  socketLinkStyles: Record<string, string | undefined>[] = [];

  private styleSnapshot = '';

  glowAlways = computed(() => {
    const force = this.forceGlow();
    if (force === true) return true;
    if (force === false) return false;
    return poe2 && this.settingsService.labelGlow() === 'always';
  });
  glowOnHover = computed(() => {
    const force = this.forceGlow();
    if (force !== null) return false;
    return poe2 && this.settingsService.labelGlow() === 'hover';
  });

  isClickableLabel = computed(() => this.clickable() && this.previewLayer() !== 'effects');

  showGlowAlways = computed(() => this.previewLayer() !== 'effects' && this.glowAlways());

  showGlowOnHover = computed(() => this.previewLayer() !== 'effects' && this.glowOnHover());

  constructor(
    private cosmeticsGeneratorService: CosmeticsGeneratorService,
    private filterStoreService: FilterStoreService,
    private itemDataService: ItemDataService,
    private settingsService: SettingsService,
    private cdr: ChangeDetectorRef,
    private navigationService: NavigationService,
    private ctrlKeyService: CtrlKeyService,
  ) {
    this.cosmeticPresets = this.filterStoreService.activeFilter().cosmeticPresets;
    this.itemColors = this.itemDataService.getItemColors();
  }

  ngOnInit(): void {
    this.showGearBaseTypeNav = this.exampleNameIsGearBaseType();
    this.showJewelleryBaseTypeNav = this.exampleNameIsJewelleryBaseType();
    this.setCosmeticOptionsFromDisplayOptions();
    this.parseSockets();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['displayOptions']) {
      this.setCosmeticOptionsFromDisplayOptions();
    }
    if (changes['sockets']) {
      this.parseSockets();
    }
    if (changes['dirtyForceUpdate']) {
      this.setCosmeticOptionsFromDisplayOptions();
    }
    if (changes['forceVisible']) {
      this.updateItemStyles();
      this.cdr.markForCheck();
    }
    if (changes['disableBeam']) {
      this.syncBeamPhase(this.disableBeam ?? false, !changes['disableBeam'].firstChange);
      this.cdr.markForCheck();
    }
    if (this.hasStyleAffectingInputChange(changes)) {
      this.updateItemStyles();
      this.cdr.markForCheck();
    }
  }

  private hasStyleAffectingInputChange(changes: SimpleChanges): boolean {
    return !!(
      changes['isCurrency'] || changes['rarity'] || changes['isDivinationCard'] ||
      changes['isQuestItem'] || changes['isPoe1Gem'] || changes['isWaystone'] || changes['forceHidden']
    );
  }

  ngDoCheck(): void {
    const showGearBaseTypeNav = this.exampleNameIsGearBaseType();
    const showJewelleryBaseTypeNav = this.exampleNameIsJewelleryBaseType();
    if (showGearBaseTypeNav !== this.showGearBaseTypeNav || showJewelleryBaseTypeNav !== this.showJewelleryBaseTypeNav) {
      this.showGearBaseTypeNav = showGearBaseTypeNav;
      this.showJewelleryBaseTypeNav = showJewelleryBaseTypeNav;
      this.cdr.markForCheck();
    }

    const snapshot = this.buildStyleSnapshot();
    if (snapshot !== this.styleSnapshot) {
      this.styleSnapshot = snapshot;
      this.setCosmeticOptionsFromDisplayOptions();
      this.cdr.markForCheck();
    }
  }

  private buildStyleSnapshot(): string {
    const d = this.displayOptions;
    // Read from displayOptions so in-place cosmetic edits are detected (cosmeticOptions is updated afterward).
    const c = d?.cosmeticOptions ?? this.cosmeticOptions;
    return `${d?.active}|${d?.hidden}|${this.forceVisible}|${this.forceHidden}|${c?.textColor}|${c?.borderColor}|${c?.backgroundColor}|${c?.fontSize}|${c?.highlight}|${c?.highlightColor}|${c?.beam}|${c?.minimapIconShape}|${c?.minimapIconSize}|${this.isCurrency}|${this.rarity}|${this.isDivinationCard}|${this.isQuestItem}|${this.isPoe1Gem}|${this.isWaystone}|${this.exampleName()}|${this.gearBaseType()}|${this.jewelleryBaseType()}`;
  }

  setCosmeticOptionsFromDisplayOptions() {
    this.cosmeticPresets = this.filterStoreService.activeFilter().cosmeticPresets;
    const result = this.cosmeticsGeneratorService.getCosmeticOptionsFromDisplayOptions(this.displayOptions, this.exampleName());
    this.cosmeticOptions = result.cosmeticOptions;
    this.hidden = result.hidden || this.forceHidden || false;
    this.updateItemStyles();
    this.refreshBeamVisibility(false);
  }

  private shouldRenderBeam(): boolean {
    if (this.previewLayer() === 'label') return false;
    return !this.hidden
      && !!this.displayOptions?.active
      && !!this.cosmeticOptions.highlight
      && !!this.cosmeticOptions.highlightColor
      && !!this.cosmeticOptions.beam;
  }

  private refreshBeamVisibility(animate: boolean): void {
    const shouldRender = this.shouldRenderBeam();
    if (shouldRender && !this.beamWasRenderable) {
      this.syncBeamPhase(this.disableBeam ?? false, animate);
    } else if (!shouldRender && this.beamWasRenderable) {
      this.beamPhase = 'idle-hidden';
    }
    this.beamWasRenderable = shouldRender;
  }

  private updateItemStyles() {
    this.disabledLabelStyle =
      (this.hidden && !this.forceVisible) || !this.displayOptions.active || (this.forceHidden ?? false);
    this.itemStyle = this.getItemStyle(false);
    this.itemStyleDisabled = this.getItemStyle(true);
    this.labelIconStyle = this.getLabelIconStyle(false);
    this.labelIconStyleDisabled = this.getLabelIconStyle(true);
  }

  onMouseEnter(event: MouseEvent) {
    if (this.previewLayer() === 'effects') return;
    this.highlightExample = true;
    this.updateItemStyles();
    this.mouseEnterLabel.emit(event);
  }

  onMouseLeave(event: MouseEvent) {
    if (this.previewLayer() === 'effects') return;
    this.highlightExample = false;
    this.updateItemStyles();
    this.mouseLeaveLabel.emit(event);
  }

  onLabelMouseDown(event: MouseEvent): void {
    if (!this.isClickableLabel()) return;
    if (event.shiftKey || event.altKey || event.ctrlKey || event.metaKey) {
      event.preventDefault();
    }
  }

  onBeamAnimationEnd(event: AnimationEvent): void {
    if (event.target !== event.currentTarget) return;
    if (this.beamPhase === 'in') {
      this.beamPhase = 'idle-visible';
      this.cdr.markForCheck();
    } else if (this.beamPhase === 'out') {
      this.beamPhase = 'idle-hidden';
      this.cdr.markForCheck();
    }
  }

  private syncBeamPhase(hidden: boolean, animate: boolean): void {
    if (!animate) {
      this.beamPhase = hidden ? 'idle-hidden' : 'in';
      return;
    }
    this.beamPhase = hidden ? 'out' : 'in';
  }

  onGearBaseTypeMouseEnter() {
    this.isGearBaseTypeHovered.set(true);
  }

  onGearBaseTypeMouseLeave() {
    this.isGearBaseTypeHovered.set(false);
  }

  onGearBaseTypeClick(event: MouseEvent) {
    const gearBaseType = this.gearBaseType();
    if (!this.settingsService.enableRuleNavigation() || !gearBaseType || !this.showGearBaseTypeNav) return;
    if (!event.ctrlKey && !event.metaKey) return;

    event.preventDefault();
    event.stopPropagation();

    this.navigationService.navigateToGearTier(gearBaseType);
  }

  onJewelleryBaseTypeMouseEnter() {
    this.isJewelleryBaseTypeHovered.set(true);
  }

  onJewelleryBaseTypeMouseLeave() {
    this.isJewelleryBaseTypeHovered.set(false);
  }

  onJewelleryBaseTypeClick(event: MouseEvent) {
    const jewelleryBaseType = this.jewelleryBaseType();
    if (!this.settingsService.enableRuleNavigation() || !jewelleryBaseType || !this.showJewelleryBaseTypeNav) return;
    if (!event.ctrlKey && !event.metaKey) return;

    event.preventDefault();
    event.stopPropagation();

    this.navigationService.navigateToJewelleryTier(jewelleryBaseType);
  }

  parseSockets() {
    this.parsedSockets = [];
    this.socketsLinked = false;

    if (!this.sockets) {
      this.socketsContainerStyle = {};
      this.socketLinks = [];
      this.socketStyles = [];
      this.socketLinkStyles = [];
      return;
    }

    let socketString = this.sockets;

    // Check if linked (starts with L)
    if (socketString.startsWith('L')) {
      this.socketsLinked = true;
      socketString = socketString.substring(1);
    }

    // Define Z-shape positions (max 2x3 grid) - true Z pattern
    const positions = [
      { row: 0, col: 0 }, // 1: Top-left
      { row: 0, col: 1 }, // 2: Top-right
      { row: 1, col: 1 }, // 3: Middle-right
      { row: 1, col: 0 }, // 4: Middle-left
      { row: 2, col: 0 }, // 5: Bottom-left
      { row: 2, col: 1 }  // 6: Bottom-right
    ];

    // Parse each socket character
    for (let i = 0; i < Math.min(socketString.length, 6); i++) {
      const socketChar = socketString[i].toUpperCase();
      let color: string;

      switch (socketChar) {
        case 'R':
          color = '#ca0d32';
          break;
        case 'G':
          color = '#9ecb0d';
          break;
        case 'B':
          color = '#5882fe';
          break;
        case 'X':
          color = '#c8c8c8';
          break;
        case 'W':
          color = '#c8c8c8';
          break;
        case 'T':
          color = 'rgba(0, 0, 0, 0)';
          break;
        default:
          continue; // Skip invalid characters
      }

      this.parsedSockets.push({
        color: color,
        position: positions[i]
      });
    }

    this.socketsContainerStyle = this.getSocketsStyle();
    this.socketLinks = this.getSocketLinks();
    this.socketStyles = this.parsedSockets.map(s => this.getSocketStyle(s));
    this.socketLinkStyles = this.socketLinks.map(l => this.getSocketLinkStyle(l.from, l.to));
  }

  getSocketsStyle() {
    const usedRows = this.getUsedRows();
    const actualHeight = this.getActualSocketsHeight(usedRows);

    return {
      'display': 'inline-block',
      'position': 'relative',
      'vertical-align': 'middle',
      'margin-left': '6px',
      'width': '8px', // 3 + 2 + 3
      'height': actualHeight + 'px'
    };
  }

  getUsedRows(): number[] {
    const rows = new Set<number>();
    this.parsedSockets.forEach(socket => {
      rows.add(socket.position.row);
    });
    return Array.from(rows).sort();
  }

  getActualSocketsHeight(usedRows: number[]): number {
    if (usedRows.length === 0) return 0;
    if (usedRows.length === 1) return 3; // Single row: just 3px for the socket
    if (usedRows.length === 2) return 8; // Two rows: 3 + 2 + 3
    return 13; // Three rows: 3 + 2 + 3 + 2 + 3
  }

  getRemappedRowPosition(originalRow: number, usedRows: number[]): number {
    // Map the original row to its position in the compacted layout
    const rowIndex = usedRows.indexOf(originalRow);
    return rowIndex * 5; // Each row is 5px apart (3px socket + 2px gap)
  }

  getSocketStyle(socket: { color: string; position: { row: number; col: number } }) {
    const usedRows = this.getUsedRows();

    const left = socket.position.col * 5; // 3px socket + 2px gap
    const top = this.getRemappedRowPosition(socket.position.row, usedRows);

    return {
      'position': 'absolute',
      'width': '3px',
      'height': '3px',
      'background-color': socket.color,
      'left': left + 'px',
      'top': top + 'px'
    };
  }

  getSocketLinkStyle(fromSocket: any, toSocket: any) {
    const usedRows = this.getUsedRows();

    const fromLeft = fromSocket.position.col * 5 + 1; // Center of socket
    const fromTop = this.getRemappedRowPosition(fromSocket.position.row, usedRows) + 1;
    const toLeft = toSocket.position.col * 5 + 1;
    const toTop = this.getRemappedRowPosition(toSocket.position.row, usedRows) + 1;

    // Only horizontal and vertical connections
    const isHorizontal = fromSocket.position.row === toSocket.position.row;
    const isVertical = fromSocket.position.col === toSocket.position.col;

    if (isHorizontal) {
      const width = Math.abs(toLeft - fromLeft);
      const left = Math.min(fromLeft, toLeft);
      return {
        'position': 'absolute',
        'width': width + 'px',
        'height': '1px',
        'background-color': 'white',
        'left': left + 'px',
        'top': fromTop + 'px'
      };
    } else if (isVertical) {
      const height = Math.abs(toTop - fromTop);
      const top = Math.min(fromTop, toTop);
      return {
        'position': 'absolute',
        'width': '1px',
        'height': height + 'px',
        'background-color': 'white',
        'left': fromLeft + 'px',
        'top': top + 'px'
      };
    }

    // For non-horizontal/vertical connections, create L-shaped path
    return {
      'display': 'none' // Hide diagonal connections for now
    };
  }

  getSocketLinks() {
    if (!this.socketsLinked || this.parsedSockets.length < 2) {
      return [];
    }

    const links = [];
    for (let i = 0; i < this.parsedSockets.length - 1; i++) {
      const from = this.parsedSockets[i];
      const to = this.parsedSockets[i + 1];

      // Check if it's a direct horizontal or vertical connection
      const fromRow = from.position.row;
      const fromCol = from.position.col;
      const toRow = to.position.row;
      const toCol = to.position.col;

      if (fromRow === toRow || fromCol === toCol) {
        // Direct connection
        links.push({ from, to });
      } else {
        // L-shaped connection: go horizontal first, then vertical
        const intermediateSocket = {
          color: 'transparent',
          position: { row: fromRow, col: toCol }
        };

        links.push({ from, to: intermediateSocket });
        links.push({ from: intermediateSocket, to });
      }
    }
    return links;
  }

  getLabelIconStyle(disabledStyle: boolean) {
    const o = disabledStyle ? this.defaultCosmeticOptions : this.cosmeticOptions;
    const fontSize = this.getFontSize(o);
    return {
      'height': fontSize + 'px',
      'width': fontSize + 'px'
    };
  }

  getItemStyle(disabledStyle: boolean) {
    const o = disabledStyle ? this.defaultCosmeticOptions : this.cosmeticOptions;

    const defaultTextColor = this.isDivinationCard ? '#0ebaff'
      : this.isQuestItem ? '#4ae63a'
      : this.exampleName() === 'Gold' ? this.itemColors.gold
      : this.isCurrency ? this.itemColors.currency
      : this.isPoe1Gem ? 'rgb(27,162,155)'
      : this.rarity === Rarity.Magic ? this.itemColors.magic
      : this.rarity === Rarity.Rare ? this.itemColors.rare
      : this.rarity === Rarity.Unique ? this.itemColors.unique
      : 'rgb(200,200,200)';

    const textColor = o?.textColor ? o.textColor : defaultTextColor;

    const borderColor = o?.borderColor ? o.borderColor
      : this.isWaystone ? defaultTextColor : null;

    const fontSize = this.getFontSize(o);

    const extraPadding = fontSize >= 16 ? 2 : fontSize >= 13 ? 1 : 0;

    const rgbComponents = this.parseColorRgb(textColor);
    const bgColor = this.getBackgroundColor(o);

    return {
      'visibility': this.formatVisibility(),
      'font-size': this.formatFontSize(fontSize),
      'line-height': this.formatLineHeight(fontSize),
      'height': 'unset !important',
      'margin-left': '4px !important',
      'margin-right': '4px !important',
      'padding': `${5}px !important`,
      'padding-top': `${5 + extraPadding}px !important`,
      'padding-bottom': `${4 + extraPadding}px !important`,
      'background': bgColor + ' !important',
      'color': this.formatColor(textColor),
      'border': this.formatBorder(borderColor, bgColor),
      'filter': this.formatFilter(),
      '--text-color': rgbComponents ? `${rgbComponents}, 1` : null,
      '--glow-text-color': rgbComponents ? `${rgbComponents}, 0.6` : null,
    };
  }

  private parseColorRgb(color: string): string | null {
    const hexMatch = color.match(/^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})/i);
    if (hexMatch) {
      return `${parseInt(hexMatch[1], 16)}, ${parseInt(hexMatch[2], 16)}, ${parseInt(hexMatch[3], 16)}`;
    }
    const rgbMatch = color.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
    if (rgbMatch) {
      return `${rgbMatch[1]}, ${rgbMatch[2]}, ${rgbMatch[3]}`;
    }
    return null;
  }

  formatFilter() {
    if (this.highlightExample) return 'brightness(1.2) !important';
    else return 'none';
  }

  private formatLineHeight(fontSize: number) {
    return (fontSize - 5) + 'px !important';
  }

  private formatBorder(borderColor: string | null, bgColor: string) {
    const color = borderColor ?? bgColor;
    return '1px solid ' + color + ' !important';
  }

  private formatColor(color: string | null) {
    return color + ' !important';
  }

  private getBackgroundColor(o?: CosmeticOptions) {
    const defaultBgColor = this.highlightExample ? '#101010f0' : '#000000f0';
    return o?.backgroundColor || defaultBgColor;
  }

  private formatVisibility() {
    return this.forceVisible ? 'visible' : this.displayOptions?.active && this.hidden ? 'hidden' : this.forceHidden ? 'hidden' : 'visible';
  }

  private getFontSize(o?: CosmeticOptions) {
    const base = o?.fontSize ?? LabelSize.Normal;
    const scaled = scaleLabelSize(base, this.cosmeticPresets.defaultLabelSize);
    return Math.round(10 + (scaled - 17) / 4);
  }

  private formatFontSize(fontSize: number) {
    return fontSize + 'px !important';
  }
}
